/** @format */

import React from 'react'
import { _$, Tag, Icon } from '@king-design/react'

/**
 * 编码字符串为Base64（支持UTF-8字符）
 */
export const encodeToBase64 = (str: string): string => {
  try {
    // 先将字符串转换为UTF-8编码，然后再进行Base64编码
    return btoa(
      encodeURIComponent(str).replace(/%([0-9A-F]{2})/g, (match, p1) =>
        String.fromCharCode(parseInt(p1, 16))
      )
    )
  } catch (e) {
    // 如果编码失败，返回原字符串
    return str
  }
}

/**
 * 解码示例代码
 * 如果字符串是Base64编码，则解码，否则返回原字符串
 * 支持UTF-8字符解码（包括中文）
 * 支持处理包含行号前缀的情况（如 "1 6L+Z5piv..."）
 */
export const decodeExample = (example: string | undefined | null): string => {
  if (!example || typeof example !== 'string') {
    return ''
  }

  // 尝试提取纯Base64部分（移除行号前缀，如 "1 "、"2 " 等）
  const extractBase64 = (str: string): string => {
    // 匹配行号模式：数字 + 空格 + Base64内容
    const lines = str.split('\n')
    const base64Lines: string[] = []

    for (const line of lines) {
      // 匹配行号前缀（如 "1 "、"123 "）
      const lineNumberMatch = line.match(/^\s*\d+\s+(.+)$/)
      if (lineNumberMatch) {
        // 提取Base64部分
        const base64Part = lineNumberMatch[1].trim()
        // 检查是否是Base64字符
        if (/^[A-Za-z0-9+/=]+$/.test(base64Part)) {
          base64Lines.push(base64Part)
        } else {
          // 如果不是Base64，保留原行
          base64Lines.push(line)
        }
      } else {
        // 没有行号前缀，检查整行是否是Base64
        const trimmedLine = line.trim()
        if (/^[A-Za-z0-9+/=]+$/.test(trimmedLine) && trimmedLine.length >= 4) {
          base64Lines.push(trimmedLine)
        } else {
          // 保留原行
          base64Lines.push(line)
        }
      }
    }

    return base64Lines.join('\n')
  }

  // 提取可能的Base64字符串
  const extractedBase64 = extractBase64(example)

  // 检查是否是Base64编码（Base64只包含 A-Z, a-z, 0-9, +, /, =）
  // 移除换行和空格后再检查
  const cleanBase64 = extractedBase64.replace(/\s+/g, '')
  const base64Regex = /^[A-Za-z0-9+/]*={0,2}$/
  const isBase64Like = base64Regex.test(cleanBase64) && cleanBase64.length >= 4

  if (isBase64Like) {
    try {
      // 先尝试整体解码（合并所有Base64行）
      const mergedBase64 = cleanBase64
      try {
        const decoded = atob(mergedBase64)

        // 尝试UTF-8解码
        try {
          // 方法1：使用 decodeURIComponent（与编码方式匹配）
          const utf8String = decoded
            .split('')
            .map(c => {
              const charCode = c.charCodeAt(0)
              return '%' + ('00' + charCode.toString(16)).slice(-2).toUpperCase()
            })
            .join('')

          const result = decodeURIComponent(utf8String)
          if (result && result.length > 0 && /[\x20-\x7E\u4E00-\u9FFF]/.test(result)) {
            return result
          }
        } catch (e) {
          // 方法1失败，尝试方法2：使用 TextDecoder
          try {
            const bytes = new Uint8Array(decoded.length)
            for (let i = 0; i < decoded.length; i++) {
              bytes[i] = decoded.charCodeAt(i)
            }
            const decoder = new TextDecoder('utf-8')
            const result = decoder.decode(bytes)
            if (result && result.length > 0) {
              return result
            }
          } catch (e2) {
            // 方法2也失败，返回原始解码结果
          }
        }

        // 如果UTF-8解码失败，但原始解码结果包含可打印字符，也返回
        if (decoded && /[\x20-\x7E\u4E00-\u9FFF]/.test(decoded)) {
          return decoded
        }
      } catch (e) {
        // 整体解码失败，尝试逐行解码
      }

      // 如果整体解码失败，尝试逐行解码
      const lines = extractedBase64.split('\n')
      const decodedLines: string[] = []
      let hasDecoded = false

      for (const line of lines) {
        const trimmedLine = line.trim()
        // 检查是否是Base64
        if (/^[A-Za-z0-9+/=]+$/.test(trimmedLine) && trimmedLine.length >= 4) {
          try {
            const decoded = atob(trimmedLine)

            // 检查解码后的内容是否包含可打印字符（包括中文、字母、数字等）
            const hasPrintableChars =
              decoded && decoded.length > 0 && /[\x20-\x7E\u4E00-\u9FFF]/.test(decoded)

            if (hasPrintableChars) {
              try {
                // 尝试解码UTF-8字符（与encodeToBase64的编码方式匹配）
                const utf8String = decoded
                  .split('')
                  .map(c => {
                    const charCode = c.charCodeAt(0)
                    return '%' + ('00' + charCode.toString(16)).slice(-2).toUpperCase()
                  })
                  .join('')

                const result = decodeURIComponent(utf8String)
                if (result && result.length > 0) {
                  decodedLines.push(result)
                  hasDecoded = true
                  continue
                }
              } catch (e) {
                // UTF-8解码失败，使用原始解码结果
              }
              decodedLines.push(decoded)
              hasDecoded = true
            } else {
              decodedLines.push(line)
            }
          } catch (e) {
            // atob失败，保留原行
            decodedLines.push(line)
          }
        } else {
          // 不是Base64，保留原行
          decodedLines.push(line)
        }
      }

      // 如果至少有一行被成功解码，返回解码结果
      if (hasDecoded) {
        return decodedLines.join('\n')
      }
    } catch (e) {
      // 解码失败，返回原字符串
    }
  }

  // 如果不是Base64编码，直接返回原字符串（可能已经是纯文本，包含中文）
  return example
}

/**
 * 渲染函数状态
 * 状态映射：
 * - 2: 启用中
 * - 1: 已新建
 * - -1: 已停用
 * - -2: 审批中
 */
export const renderFunctionStatus = (status: number | undefined, approving?: boolean) => {
  // 如果正在审批中，优先显示审批中状态
  if (approving || status === -2) {
    return (
      <Tag
        border="solid"
        style={{ backgroundColor: '#e6f9ff', borderColor: '#7ad9ff', color: '#0091ea' }}
      >
        <Icon style={{ color: '#0091ea' }} className="k-icon-user" />
        <span style={{ marginLeft: 2 }}>{_$('审批中')}</span>
      </Tag>
    )
  }

  switch (status) {
    case 2:
      return (
        <Tag border="solid" type="primary">
          <Icon style={{ color: '#0091ea' }} className="k-icon-refresh" />
          <span style={{ marginLeft: 2 }}>{_$('启用中')}</span>
        </Tag>
      )
    case 1:
      return (
        <Tag border="solid" type="default">
          <Icon className="k-icon-time" />
          <span style={{ marginLeft: 2 }}>{_$('已新建')}</span>
        </Tag>
      )
    case -1:
      return (
        <Tag border="solid" type="danger">
          {_$('已停用')}
        </Tag>
      )
    default:
      return (
        <Tag border="solid" type="default">
          <Icon className="k-icon-time" />
          <span style={{ marginLeft: 2 }}>{_$('已新建')}</span>
        </Tag>
      )
  }
}
